// ======================================================
// MOVIEFRENZY — WIKIPEDIA / MEDIWIKI
// Recherche du rôle d'un acteur dans un film
//
// Phase 1 :
// - trouver l'article Wikipédia du film
// - trouver la ligne de distribution de l'acteur
// - extraire le personnage
// ======================================================


const SUPPORTED_LANGUAGES = [
    "fr",
    "en",
    "es",
    "de",
];


const normalizeLanguage = (language) => {
    const shortLanguage =
        String(language || "fr")
            .split("-")[0]
            .toLowerCase();

    return SUPPORTED_LANGUAGES.includes(
        shortLanguage
    )
        ? shortLanguage
        : "en";
};


const normalizeText = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, " ")
        .trim();


const cleanText = (value) =>
    String(value || "")
        .replace(/\s+/g, " ")
        .trim();

// ======================================================
// RECONNAÎTRE UNE VARIANTE DU NOM DE L'ACTEUR
//
// Exemple :
// TMDB     → Charlie Chaplin
// Wikipédia ES → Charles Chaplin
//
// On reste volontairement prudent :
// - même nom de famille obligatoire
// - prénom identique ou très proche
// ======================================================

const levenshteinDistance = (
    valueA,
    valueB
) => {

    const a = normalizeText(valueA);
    const b = normalizeText(valueB);

    if (a === b) {
        return 0;
    }

    if (!a.length) {
        return b.length;
    }

    if (!b.length) {
        return a.length;
    }

    const matrix =
        Array.from(
            { length: b.length + 1 },
            () =>
                Array(
                    a.length + 1
                ).fill(0)
        );

    for (
        let i = 0;
        i <= b.length;
        i += 1
    ) {
        matrix[i][0] = i;
    }

    for (
        let j = 0;
        j <= a.length;
        j += 1
    ) {
        matrix[0][j] = j;
    }

    for (
        let i = 1;
        i <= b.length;
        i += 1
    ) {
        for (
            let j = 1;
            j <= a.length;
            j += 1
        ) {

            const cost =
                b[i - 1] === a[j - 1]
                    ? 0
                    : 1;

            matrix[i][j] =
                Math.min(
                    matrix[i - 1][j] + 1,
                    matrix[i][j - 1] + 1,
                    matrix[i - 1][j - 1] + cost
                );
        }
    }

    return matrix[b.length][a.length];
};


const areActorNamesCompatible = (
    wikipediaName,
    actorName
) => {

    const wikiTokens =
        normalizeText(wikipediaName)
            .split(" ")
            .filter(Boolean);

    const actorTokens =
        normalizeText(actorName)
            .split(" ")
            .filter(Boolean);

    if (
        wikiTokens.length < 2 ||
        actorTokens.length < 2
    ) {
        return false;
    }

    const wikiLastName =
        wikiTokens[wikiTokens.length - 1];

    const actorLastName =
        actorTokens[actorTokens.length - 1];

    // Le nom de famille doit correspondre exactement.
    if (wikiLastName !== actorLastName) {
        return false;
    }

    const wikiFirstName =
        wikiTokens[0];

    const actorFirstName =
        actorTokens[0];

    return (
        wikiFirstName === actorFirstName ||
        levenshteinDistance(
            wikiFirstName,
            actorFirstName
        ) <= 2
    );
};


const findActorNameMatch = ({
    actorLine,
    actorName,
}) => {

    if (!actorLine || !actorName) {
        return null;
    }

    const line =
        cleanText(actorLine);

    // --------------------------------------------------
    // 1. PRIORITÉ AU NOM PLACÉ AVANT LE PREMIER ":"
    //
    // Charles Chaplin: Charlot (...)
    //
    // On évite ainsi de trouver accidentellement
    // "Charlie Chaplin" plus loin dans la parenthèse.
    // --------------------------------------------------

    const colonIndex =
        line.indexOf(":");

    if (colonIndex > 0) {

        const leadingName =
            cleanText(
                line.slice(
                    0,
                    colonIndex
                )
            );

        if (
            areActorNamesCompatible(
                leadingName,
                actorName
            )
        ) {
            return {
                index: 0,
                length: leadingName.length,
                matchedName: leadingName,
                type: "compatible-leading-name",
            };
        }
    }


    // --------------------------------------------------
    // 2. COMPORTEMENT NORMAL :
    // correspondance exacte du nom TMDB.
    // --------------------------------------------------

    const exactIndex =
        line
            .toLowerCase()
            .indexOf(
                actorName.toLowerCase()
            );

    if (exactIndex !== -1) {
        return {
            index: exactIndex,
            length: actorName.length,
            matchedName: actorName,
            type: "exact",
        };
    }


    return null;
};


// ======================================================
// NETTOYER UNE NOTE DE CASTING APRÈS LE PERSONNAGE
//
// Exemple ES :
// Charlot (anunciado también como Charlie Chaplin
// y protagonista)
//
// devient :
// Charlot
// ======================================================

const cleanCharacterCandidate = ({
    value,
    actorName,
}) => {

    const text =
        cleanText(value);

    const trailingParenthesis =
        text.match(
            /^(.*?)\s*\(([^)]*)\)\s*$/
        );

    if (!trailingParenthesis) {
        return text;
    }

    const beforeParenthesis =
        cleanText(
            trailingParenthesis[1]
        );

    const note =
        normalizeText(
            trailingParenthesis[2]
        );

    const actorLastName =
        normalizeText(actorName)
            .split(" ")
            .filter(Boolean)
            .pop();

    const metadataMarkers = [
        "protagonista",
        "protagonist",
        "anunciado",
        "anunciada",
        "tambien",
        "credited",
        "uncredited",
        "voice",
        "voz",
        "stimme",
    ];

    const looksLikeCastingMetadata =
        (
            actorLastName &&
            note.includes(actorLastName)
        ) ||
        metadataMarkers.some(
            (marker) =>
                note.includes(marker)
        );

    return looksLikeCastingMetadata
        ? beforeParenthesis
        : text;
};


// ======================================================
// 1. CHERCHER L'ARTICLE DU FILM
// ======================================================

const searchWikipediaMoviePage = async ({
    movie,
    language,
}) => {
    const wikiLanguage =
        normalizeLanguage(language);

    const apiUrl =
        `https://${wikiLanguage}.wikipedia.org/w/api.php`;

    const movieTitle =
        movie?.title ||
        movie?.original_title;

    if (!movieTitle) {
        return null;
    }

    const year =
        movie?.release_date
            ? movie.release_date.slice(0, 4)
            : "";

    const filmWord = {
        fr: "film",
        en: "film",
        es: "película",
        de: "Film",
    }[wikiLanguage];


    const searchQuery =
        `${movieTitle} ${year} ${filmWord}`.trim();


    const params =
        new URLSearchParams({
            origin: "*",
            action: "query",
            list: "search",
            srsearch: searchQuery,
            srnamespace: "0",
            srlimit: "5",
            format: "json",
            utf8: "1",
        });


    const response =
        await fetch(
            `${apiUrl}?${params.toString()}`
        );


    if (!response.ok) {
        throw new Error(
            `Wikipedia search HTTP ${response.status}`
        );
    }


    const data =
        await response.json();


    const results =
        data?.query?.search || [];


    if (!results.length) {
        return null;
    }


    // Pour cette première version :
    // on prend le meilleur résultat retourné
    // par le moteur de recherche Wikipédia.
    const firstResult =
        results[0];


    return {
        apiUrl,
        language: wikiLanguage,
        pageTitle: firstResult.title,
    };
};


// ======================================================
// 2. RÉCUPÉRER LE HTML DE L'ARTICLE
// ======================================================

const fetchWikipediaPageHtml = async ({
    apiUrl,
    pageTitle,
}) => {
    const params =
        new URLSearchParams({
            origin: "*",
            action: "parse",
            page: pageTitle,
            prop: "text",
            format: "json",
            utf8: "1",
        });


    const response =
        await fetch(
            `${apiUrl}?${params.toString()}`
        );


    if (!response.ok) {
        throw new Error(
            `Wikipedia parse HTTP ${response.status}`
        );
    }


    const data =
        await response.json();


    // Compatible avec les deux formes
    // rencontrées dans les réponses MediaWiki.
    return (
        data?.parse?.text?.["*"] ||
        data?.parse?.text ||
        null
    );
};


// ======================================================
// 3. TROUVER LA LIGNE DE L'ACTEUR
// ======================================================

const findActorLine = ({
    html,
    actorName,
}) => {
    if (!html || !actorName) {
        return null;
    }


    const documentNode =
        new DOMParser()
            .parseFromString(
                html,
                "text/html"
            );


    const normalizedActorName =
        normalizeText(actorName);


    // Les distributions Wikipédia sont
    // très souvent des <li>.
    const listItems =
        Array.from(
            documentNode.querySelectorAll("li")
        );

    const listMatch =
        listItems.find((element) => {

            const actorLine =
                cleanText(
                    element.textContent
                );

            return Boolean(
                findActorNameMatch({
                    actorLine,
                    actorName,
                })
            );
        });


    if (listMatch) {
        return cleanText(
            listMatch.textContent
        );
    }


    // Fallback pour certaines pages
    // utilisant des tableaux.
    const tableRows =
        Array.from(
            documentNode.querySelectorAll("tr")
        );


    const tableMatch =
        tableRows.find((element) => {

            const actorLine =
                cleanText(
                    element.textContent
                );

            return Boolean(
                findActorNameMatch({
                    actorLine,
                    actorName,
                })
            );
        });


    if (tableMatch) {
        return cleanText(
            tableMatch.textContent
        );
    }


    return null;
};


// ======================================================
// 4. EXTRAIRE LE PERSONNAGE
// ======================================================

const extractCharacterFromActorLine = ({
    actorLine,
    actorName,
    language,
}) => {
    if (!actorLine) {
        return {
            character: null,
            description: null,
        };
    }

    const actorMatch =
        findActorNameMatch({
            actorLine,
            actorName,
        });


    if (!actorMatch) {
        return {
            character: null,
            description: null,
        };
    }


    let remainder =
        actorLine
            .slice(
                actorMatch.index +
                actorMatch.length
            )
            .trim();


    // Exemple FR :
    //
    // Robert Downey Jr.
    // (VF et VQ : Bernard Gabay)
    // : Tony Stark / Iron Man
    //
    // On retire d'abord le bloc
    // de doublage éventuel.
    remainder =
        remainder.replace(
            /^\s*\([^)]*\)\s*/,
            ""
        );


    remainder =
        remainder.trim();


    // Français / espagnol / allemand :
    if (remainder.startsWith(":")) {
        remainder =
            remainder
                .slice(1)
                .trim();
    }


    // Wikipédia anglais :
    //
    // Robert Downey Jr. as Tony Stark / Iron Man:
    // description...
    if (
        normalizeLanguage(language) === "en"
    ) {
        remainder =
            remainder.replace(
                /^as\s+/i,
                ""
            );
    }


    const colonIndex =
        remainder.indexOf(":");


    if (colonIndex !== -1) {
        return {
            character:
                cleanText(
                    remainder.slice(
                        0,
                        colonIndex
                    )
                ),

            description:
                cleanText(
                    remainder.slice(
                        colonIndex + 1
                    )
                ) || null,
        };
    }


    return {
        character:
            cleanCharacterCandidate({
                value: remainder,
                actorName,
            }) || null,

        description: null,
    };
};

const extractRoleBeforeCharacter = ({
    actorLine,
    actorName,
    character,
    language,
}) => {

    if (
        !actorLine ||
        !actorName ||
        !character
    ) {
        return null;
    }


    const actorMatch =
        findActorNameMatch({
            actorLine,
            actorName,
        });


    if (!actorMatch) {
        return null;
    }


    let remainder =
        actorLine
            .slice(
                actorMatch.index +
                actorMatch.length
            )
            .trim();


    // Exemple anglais :
    //
    // Roy Dupuis as Narcotics Officer Gilles Levac
    //
    // devient :
    //
    // Narcotics Officer Gilles Levac
    if (
        normalizeLanguage(language) === "en"
    ) {
        remainder =
            remainder.replace(
                /^as\s+/i,
                ""
            );
    }


    remainder =
        remainder
            .replace(
                /^[:\-–—]\s*/,
                ""
            )
            .trim();


    const escapedCharacter =
        character.replace(
            /[.*+?^${}()|[\]\\]/g,
            "\\$&"
        );


    const characterAtEnd =
        new RegExp(
            `${escapedCharacter}\\s*$`,
            "i"
        );


    // Si la ligne se termine bien
    // par le personnage connu,
    // tout ce qui le précède peut
    // constituer la description du rôle.
    if (
        !characterAtEnd.test(remainder)
    ) {
        return null;
    }


    const role =
        remainder
            .replace(
                characterAtEnd,
                ""
            )
            .trim();


    return role || null;
};

// ======================================================
// TRADUIRE LE RÔLE POUR LA LANGUE DE L'INTERFACE
// ======================================================

const ROLE_TRANSLATIONS = {
    "en:fr": {
        "narcotics officer": "Policier aux stupéfiants",
    },
};


const translateRoleForUi = ({
    role,
    sourceLanguage,
    targetLanguage,
}) => {

    if (!role) {
        return null;
    }


    const source =
        normalizeLanguage(sourceLanguage);

    const target =
        normalizeLanguage(targetLanguage);


    // Rien à traduire si la langue source
    // correspond déjà à celle de l'interface.
    if (source === target) {
        return role;
    }


    const translations =
        ROLE_TRANSLATIONS[
        `${source}:${target}`
        ];


    if (!translations) {
        return role;
    }


    return (
        translations[
        normalizeText(role)
        ] ||
        role
    );
};


// ======================================================
// 5. EXTRAIRE L'IMPLICATION DU PERSONNAGE
//    DEPUIS LE SYNOPSIS / PLOT
// ======================================================

const NARRATIVE_SECTION_TITLES = {
    fr: [
        "synopsis",
        "résumé",
        "intrigue",
    ],

    en: [
        "plot",
        "synopsis",
    ],

    es: [
        "argumento",
        "sinopsis",
    ],

    de: [
        "handlung",
        "inhalt",
    ],
};


const splitIntoSentences = (
    text,
    language
) => {
    if (!text) {
        return [];
    }


    // Les navigateurs modernes disposent
    // d'un découpage linguistique natif.
    if (
        typeof Intl !== "undefined" &&
        typeof Intl.Segmenter === "function"
    ) {
        try {
            const segmenter =
                new Intl.Segmenter(
                    language,
                    {
                        granularity: "sentence",
                    }
                );


            return Array
                .from(
                    segmenter.segment(text)
                )
                .map((item) =>
                    cleanText(item.segment)
                )
                .filter(Boolean);

        } catch (error) {
            // On utilisera le fallback
            // ci-dessous.
        }
    }


    return (
        text.match(
            /[^.!?…]+(?:[.!?…]+|$)/g
        ) || []
    )
        .map(cleanText)
        .filter(Boolean);
};


const buildCharacterSearchTerms = (
    character
) => {
    if (!character) {
        return [];
    }


    // Exemples :
    //
    // Tony Stark / Iron Man
    // → Tony Stark
    // → Iron Man
    // → Stark
    //
    // Ki-taek, le père
    // → Ki-taek
    //
    // IMPORTANT :
    // "père" ne doit PAS devenir un terme de recherche.

    const parts =
        String(character)
            .split(
                /\s*\/\s*|\s+alias\s+/i
            )
            .map(cleanText)
            .filter(Boolean);


    const coreParts =
        parts
            .map((part) => {

                // Wikipédia ajoute parfois une précision
                // après le véritable nom du personnage :
                //
                // "Ki-taek, le père"
                // "Dong-ik, le père"
                //
                // Pour rechercher dans le synopsis,
                // on conserve aussi seulement :
                //
                // "Ki-taek"
                // "Dong-ik"

                return cleanText(
                    part.replace(
                        /\s*[,;(].*$/,
                        ""
                    )
                );
            })
            .filter(Boolean);


    const terms =
        new Set([
            ...parts,
            ...coreParts,
        ]);


    // Pour les noms composés classiques :
    //
    // Tony Stark
    // → ajoute aussi Stark
    //
    // Gilles Levac
    // → ajoute aussi Levac
    //
    // François-Marie-Thomas Chevalier de Lorimier
    // → ajoute aussi Lorimier

    const firstCorePart =
        coreParts[0] ||
        parts[0] ||
        "";


    const firstPartWords =
        firstCorePart
            .split(/\s+/)
            .filter(Boolean);


    if (firstPartWords.length >= 2) {

        const lastName =
            firstPartWords[
            firstPartWords.length - 1
            ];


        if (lastName.length >= 4) {
            terms.add(lastName);
        }
    }


    return Array.from(terms);
};

// ======================================================
// EXTRAIRE UN QUALIFICATIF DU PERSONNAGE
// ======================================================

const extractCharacterQualifier = ({
    character,
    language,
}) => {
    if (!character) {
        return null;
    }

    const parts =
        String(character)
            .split(",")
            .map(cleanText)
            .filter(Boolean);

    // Il faut impérativement une précision après une virgule.
    //
    // Exemple :
    // "Ki-taek, le père"
    //
    // Mais :
    // "Tony Stark / Iron Man"
    // "Gilles Levac"
    //
    // ne doivent rien retourner.
    if (parts.length < 2) {
        return null;
    }

    const qualifier =
        parts
            .slice(1)
            .join(", ")
            .trim();

    if (!qualifier) {
        return null;
    }

    const normalizedQualifier =
        normalizeText(qualifier);

    const knownQualifiers = {
        fr: {
            "le pere": "Père",
            "la mere": "Mère",
            "le fils": "Fils",
            "la fille": "Fille",
        },

        en: {
            "the father": "Father",
            "father": "Father",
            "the mother": "Mother",
            "mother": "Mother",
            "the son": "Son",
            "son": "Son",
            "the daughter": "Daughter",
            "daughter": "Daughter",
        },

        es: {
            "el padre": "Padre",
            "la madre": "Madre",
            "el hijo": "Hijo",
            "la hija": "Hija",
        },

        de: {
            "der vater": "Vater",
            "die mutter": "Mutter",
            "der sohn": "Sohn",
            "die tochter": "Tochter",
        },
    };

    const wikiLanguage =
        normalizeLanguage(language);

    return (
        knownQualifiers[wikiLanguage]?.[
        normalizedQualifier
        ] ||
        null
    );
};


const findNarrativeSectionText = ({
    html,
    language,
}) => {
    if (!html) {
        return null;
    }


    const documentNode =
        new DOMParser()
            .parseFromString(
                html,
                "text/html"
            );


    const wikiLanguage =
        normalizeLanguage(language);


    const expectedTitles =
        (
            NARRATIVE_SECTION_TITLES[
            wikiLanguage
            ] ||
            NARRATIVE_SECTION_TITLES.en
        )
            .map(normalizeText);


    const headings =
        Array.from(
            documentNode.querySelectorAll(
                "h2, h3"
            )
        );


    const narrativeHeading =
        headings.find((heading) => {

            const headline =
                heading.querySelector(
                    ".mw-headline"
                );


            const headingText =
                normalizeText(
                    headline?.textContent ||
                    heading.textContent
                );


            return expectedTitles.some(
                (title) =>
                    headingText === title ||
                    headingText.startsWith(
                        `${title} `
                    )
            );
        });


    if (!narrativeHeading) {
        return null;
    }


    // Wikipédia peut envelopper les titres
    // dans un conteneur .mw-heading.
    const headingContainer =
        narrativeHeading.closest(
            ".mw-heading"
        ) ||
        narrativeHeading;


    let currentNode =
        headingContainer.nextElementSibling;


    const paragraphs = [];


    while (currentNode) {

        // Arrivée à la section suivante :
        // on arrête la lecture.
        if (
            currentNode.matches?.(
                "h2, h3"
            ) ||
            currentNode.classList?.contains(
                "mw-heading"
            )
        ) {
            break;
        }


        if (
            currentNode.matches?.("p")
        ) {
            const text =
                cleanText(
                    currentNode.textContent
                );

            if (text) {
                paragraphs.push(text);
            }

        } else {

            const nestedParagraphs =
                Array.from(
                    currentNode.querySelectorAll?.(
                        "p"
                    ) || []
                );


            nestedParagraphs.forEach(
                (paragraph) => {

                    const text =
                        cleanText(
                            paragraph.textContent
                        );


                    if (text) {
                        paragraphs.push(text);
                    }
                }
            );
        }


        currentNode =
            currentNode.nextElementSibling;
    }


    return paragraphs.length
        ? paragraphs.join(" ")
        : null;
};


const extractCharacterStory = ({
    html,
    character,
    language,
}) => {
    if (!html || !character) {
        return null;
    }


    const narrativeText =
        findNarrativeSectionText({
            html,
            language,
        });


    if (!narrativeText) {
        return null;
    }


    const sentences =
        splitIntoSentences(
            narrativeText,
            normalizeLanguage(language)
        );


    const searchTerms =
        buildCharacterSearchTerms(
            character
        )
            .map(normalizeText)
            .filter(Boolean);


    if (!searchTerms.length) {
        return null;
    }


    // ======================================================
    // CLASSER LES PHRASES PAR PERTINENCE
    //
    // On ne veut plus simplement prendre
    // les premières phrases contenant le personnage.
    //
    // Une phrase est considérée plus pertinente si :
    // - le nom du personnage apparaît très tôt ;
    // - un terme précis comme "Tony Stark"
    //   ou "Iron Man" est trouvé ;
    // - la phrase n'est pas démesurément longue.
    // ======================================================

    const scoredSentences =
        sentences
            .map((sentence, originalIndex) => {

                const normalizedSentence =
                    normalizeText(sentence);


                const matches =
                    searchTerms
                        .map((term) => ({
                            term,
                            index:
                                normalizedSentence.indexOf(
                                    term
                                ),
                        }))
                        .filter(
                            ({ index }) =>
                                index !== -1
                        );


                if (!matches.length) {
                    return null;
                }


                // ======================================================
                // PROTECTION ANTI-PASSAGE TROP TARDIF
                //
                // On évite d'utiliser comme "story"
                // une phrase située trop loin dans le synopsis.
                // 0.55 = environ les 55 premiers % du récit.
                // ======================================================

                const relativeStoryPosition =
                    sentences.length > 1
                        ? originalIndex / (sentences.length - 1)
                        : 0;


                if (relativeStoryPosition > 0.55) {

                    console.log(
                        "🛡️ MovieFrenzy story rejetée : passage trop tardif",
                        {
                            character,
                            language:
                                normalizeLanguage(language),

                            originalIndex,

                            totalSentences:
                                sentences.length,

                            position:
                                Math.round(
                                    relativeStoryPosition * 100
                                ) + "%",

                            sentence,
                        }
                    );

                    return null;
                }


                const firstMatchIndex =
                    Math.min(
                        ...matches.map(
                            ({ index }) => index
                        )
                    );


                const longestMatchedTerm =
                    Math.max(
                        ...matches.map(
                            ({ term }) =>
                                term.length
                        )
                    );


                let score = 0;


                // ------------------------------------------
                // 1. Position du personnage dans la phrase
                // ------------------------------------------

                if (firstMatchIndex === 0) {

                    score += 100;

                } else if (firstMatchIndex <= 35) {

                    score += 70;

                } else if (firstMatchIndex <= 90) {

                    score += 40;

                } else {

                    score += 10;
                }


                // ------------------------------------------
                // 2. Favoriser un nom précis
                //
                // "Tony Stark" vaut davantage que
                // le simple terme "Stark".
                // ------------------------------------------

                if (longestMatchedTerm >= 8) {
                    score += 20;
                }


                // ------------------------------------------
                // 3. Éviter de favoriser les énormes phrases
                // Wikipédia contenant dix personnages.
                // ------------------------------------------

                if (sentence.length <= 260) {

                    score += 15;

                } else if (sentence.length > 500) {

                    score -= 20;
                }


                return {
                    sentence,
                    score,
                    originalIndex,
                };
            })
            .filter(Boolean)
            .sort(
                (a, b) =>
                    b.score - a.score ||
                    a.originalIndex - b.originalIndex
            );


    if (!scoredSentences.length) {
        return null;
    }


    // ======================================================
    // PROTECTION DE CONTINUITÉ ENTRE LES PHRASES
    //
    // On conserve toujours la meilleure phrase.
    //
    // Une deuxième phrase n'est ajoutée que si elle se trouve
    // très près de la première dans le synopsis.
    //
    // Cela évite par exemple :
    // début du film + scène située beaucoup plus tard.
    // ======================================================

    const bestSentence =
        scoredSentences[0];

    const MAX_SENTENCE_GAP = 2;

    const secondNearbySentence =
        scoredSentences
            .slice(1)
            .find(
                (candidate) =>
                    Math.abs(
                        candidate.originalIndex -
                        bestSentence.originalIndex
                    ) <= MAX_SENTENCE_GAP
            );


    const selectedSentences =
        secondNearbySentence
            ? [
                bestSentence,
                secondNearbySentence,
            ]
            : [
                bestSentence,
            ];


    if (
        scoredSentences.length > 1 &&
        !secondNearbySentence
    ) {
        console.log(
            "🧩 MovieFrenzy story limitée : deuxième passage trop éloigné",
            {
                character,
                language:
                    normalizeLanguage(language),

                bestIndex:
                    bestSentence.originalIndex,

                rejectedCandidates:
                    scoredSentences
                        .slice(1, 4)
                        .map(
                            ({
                                originalIndex,
                                sentence,
                            }) => ({
                                originalIndex,
                                distance:
                                    Math.abs(
                                        originalIndex -
                                        bestSentence.originalIndex
                                    ),
                                sentence,
                            })
                        ),
            }
        );
    }


    // On remet les phrases dans leur ordre narratif.
    return selectedSentences
        .sort(
            (a, b) =>
                a.originalIndex -
                b.originalIndex
        )
        .map(
            ({ sentence }) =>
                sentence
        )
        .join(" ");
};


// ======================================================
// 6. CONSTRUIRE UN RÔLE NARRATIF COURT
// ======================================================

const buildNarrativeRole = ({
    character,
    description,
    story,
}) => {

    // --------------------------------------------------
    // 1. Choisir uniquement une matière Wikipédia réelle
    // --------------------------------------------------

    const sourceText =
        description;

    if (!sourceText || !character) {
        return null;
    }


    // --------------------------------------------------
    // 2. Garder seulement la première phrase pertinente
    // --------------------------------------------------

    const firstSentence =
        sourceText
            .match(
                /[^.!?…]+(?:[.!?…]+|$)/g
            )
            ?.map(cleanText)
            .filter(Boolean)[0] ||
        null;


    if (!firstSentence) {
        return null;
    }


    // --------------------------------------------------
    // 3. Couper une seconde proposition indépendante
    //
    // Exemple :
    // "... Tesseract et Rogers arrive à son tour"
    //
    // devient :
    // "... Tesseract"
    //
    // Fonctionne également avec :
    // and / y / und
    // --------------------------------------------------

    let shortRole =
        firstSentence
            .split(
                /\s+(?:et|and|y|und)\s+(?=[A-ZÀ-ÖØ-Þ])/u
            )[0]
            .trim();


    // --------------------------------------------------
    // 4. Éviter un "rôle" beaucoup trop long
    // --------------------------------------------------

    const MAX_ROLE_LENGTH = 115;

    if (
        shortRole.length >
        MAX_ROLE_LENGTH
    ) {

        shortRole =
            shortRole
                .slice(
                    0,
                    MAX_ROLE_LENGTH
                )
                .replace(
                    /\s+\S*$/,
                    ""
                )
                .trim();

        shortRole += "…";

    } else {

        // Le champ est une courte formulation,
        // pas nécessairement une phrase complète.
        shortRole =
            shortRole.replace(
                /[.!?…]+$/,
                ""
            );
    }


    return shortRole || null;
};


// ======================================================
// API PUBLIQUE MOVIEFRENZY
// ======================================================

export const fetchActorMovieRoleFromWikipedia =
    async ({
        movie,
        actorName,
        language = "fr",
    }) => {

        if (!movie?.id || !actorName) {
            return null;
        }


        const page =
            await searchWikipediaMoviePage({
                movie,
                language,
            });


        if (!page) {
            return null;
        }


        const html =
            await fetchWikipediaPageHtml({
                apiUrl: page.apiUrl,
                pageTitle: page.pageTitle,
            });


        if (!html) {
            return null;
        }


        const actorLine =
            findActorLine({
                html,
                actorName,
            });


        if (!actorLine) {
            return {
                found: false,

                movieId: movie.id,
                actorName,

                pageTitle:
                    page.pageTitle,

                sourceUrl:
                    `https://${page.language}.wikipedia.org/wiki/${encodeURIComponent(
                        page.pageTitle.replace(
                            / /g,
                            "_"
                        )
                    )}`,
            };
        }


        const {
            character,
            description,
        } =
            extractCharacterFromActorLine({
                actorLine,
                actorName,
                language:
                    page.language,
            });

        const story =
            extractCharacterStory({
                html,
                character,
                language:
                    page.language,
            });

        let narrativeRole =
            buildNarrativeRole({
                character,
                description,
                story,
            });


        let alternateRoleData = null;


        // ======================================================
        // FALLBACK WIKIPÉDIA ANGLAIS
        //
        // Certaines éditions de Wikipédia possèdent
        // une distribution plus détaillée.
        //
        // Exemple :
        // FR : Roy Dupuis : Gilles Levac
        // EN : Roy Dupuis as Narcotics Officer Gilles Levac
        // ======================================================

        if (
            !narrativeRole &&
            character &&
            page.language !== "en"
        ) {

            try {

                const englishPage =
                    await searchWikipediaMoviePage({
                        movie,
                        language: "en",
                    });


                if (englishPage) {

                    const englishHtml =
                        await fetchWikipediaPageHtml({
                            apiUrl:
                                englishPage.apiUrl,

                            pageTitle:
                                englishPage.pageTitle,
                        });


                    const englishActorLine =
                        findActorLine({
                            html:
                                englishHtml,

                            actorName,
                        });


                    const englishRole =
                        extractRoleBeforeCharacter({
                            actorLine:
                                englishActorLine,

                            actorName,

                            character,

                            language: "en",
                        });

                    if (englishRole) {

                        narrativeRole =
                            englishRole;


                        alternateRoleData = {
                            language: "en",

                            pageTitle:
                                englishPage.pageTitle,

                            actorLine:
                                englishActorLine,
                        };
                    }
                }

            } catch (error) {

                console.warn(
                    "MovieFrenzy Wikipedia EN role fallback:",
                    error
                );
            }
        }

        // ======================================================
        // FALLBACK : QUALIFICATIF DU PERSONNAGE
        //
        // Exemple :
        // "Ki-taek, le père"
        // → "Père"
        // ======================================================

        if (!narrativeRole && character) {

            narrativeRole =
                extractCharacterQualifier({
                    character,
                    language: page.language,
                });
        }

        const roleSourceLanguage =
            alternateRoleData?.language ||
            page.language;


        const localizedRole =
            translateRoleForUi({
                role: narrativeRole,
                sourceLanguage:
                    roleSourceLanguage,
                targetLanguage:
                    language,
            });

        console.log(
            "MovieFrenzy Wikipedia role:",
            {
                movieId: movie.id,
                movieTitle: movie.title,
                actorName,
                character,

                roleOriginal:
                    narrativeRole,

                role:
                    localizedRole,

                roleSourceLanguage,

                alternateRoleData,
            }
        );

        return {
            found: true,

            movieId:
                movie.id,

            movieTitle:
                movie.title,

            actorName,

            character,

            // Si la ligne de distribution contient
            // une véritable description du rôle,
            // elle sera disponible ici.
            // Pour Avengers FR, elle est actuellement null.
            role: localizedRole,

            roleOriginal:
                narrativeRole,

            roleSourceLanguage,

            // Temporaire
            roleFallback:
                alternateRoleData,

            // Phrases réellement trouvées
            // dans le synopsis Wikipédia.
            story,

            description,
            actorLine,

            pageTitle:
                page.pageTitle,

            language:
                page.language,

            sourceUrl:
                `https://${page.language}.wikipedia.org/wiki/${encodeURIComponent(
                    page.pageTitle.replace(
                        / /g,
                        "_"
                    )
                )}`,
        };
    };